import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout, { StatCard } from "@/components/DashboardLayout";
import UsageBar from "@/components/UsageBar";
import { motion } from "framer-motion";
import {
  EnvelopeSimple, Users, ChatCircleDots, ArrowRight, Lightning,
  CheckCircle, Clock,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => setUsage(null));
  }, []);

  return (
    <DashboardLayout
      title="Today on the floor"
      actions={
        <Link to="/dashboard/email" className="btn-primary text-sm" data-testid="quick-email-btn">
          <Lightning size={16} weight="fill" /> Run email triage
        </Link>
      }
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Usage bar */}
        {usage && (
          <div className="col-span-12">
            <UsageBar usage={usage} compact />
          </div>
        )}
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:.3}} className="col-span-12 md:col-span-3">
          <StatCard label="Emails processed" value={stats?.emails_processed ?? "—"} hint={`of ${stats?.emails_total ?? 0} in inbox`} />
        </motion.div>
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:.3,delay:.05}} className="col-span-12 md:col-span-3">
          <StatCard label="Leads scored" value={stats?.leads_processed ?? "—"} hint={`${stats?.leads_good ?? 0} marked good`} accent />
        </motion.div>
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:.3,delay:.1}} className="col-span-12 md:col-span-3">
          <StatCard label="Chats handled" value={stats?.chats_handled ?? "—"} hint="Across all sessions" />
        </motion.div>
        <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:.3,delay:.15}} className="col-span-12 md:col-span-3">
          <div className="cell-strong p-6 bg-[var(--ink)] text-white h-full">
            <div className="overline text-white/50">Hours saved (est.)</div>
            <div className="text-5xl font-display mt-2" data-testid="stat-hours-saved">
              {((stats?.emails_processed || 0) * 4 + (stats?.leads_processed || 0) * 2 + (stats?.chats_handled || 0) * 3) / 60 | 0}h
            </div>
            <div className="text-sm text-white/60 mt-2">This week's headcount math.</div>
          </div>
        </motion.div>

        {/* Modules */}
        <Module to="/dashboard/email" icon={EnvelopeSimple} n="01" title="Email Automation" copy="Triage inbox, draft replies." span="col-span-12 lg:col-span-4" />
        <Module to="/dashboard/leads" icon={Users} n="02" title="Lead Processing" copy="Clean, dedupe, score, route." span="col-span-12 lg:col-span-4" accent />
        <Module to="/dashboard/bot" icon={ChatCircleDots} n="03" title="Customer Bot" copy="Answer FAQs, capture leads." span="col-span-12 lg:col-span-4" dark />

        {/* Activity feed */}
        <div className="col-span-12 lg:col-span-8 cell-strong bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-[var(--ink-soft)]">/ Activity</div>
              <div className="font-display text-2xl mt-1">Recent work by your AI employee</div>
            </div>
            <span className="tag tag-yellow">LIVE</span>
          </div>
          {stats?.recent_activity?.length ? (
            <ul className="divide-y divide-[var(--line)]">
              {stats.recent_activity.map((a, i) => (
                <li key={i} className="py-3 flex items-start gap-3" data-testid={`activity-row-${i}`}>
                  <KindIcon kind={a.kind} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{a.message}</div>
                    <div className="text-xs text-[var(--ink-soft)] font-mono-rc mt-0.5">{new Date(a.ts).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[var(--ink-soft)] py-6">Nothing yet — run a triage from the Email module.</div>
          )}
        </div>

        {/* Quick actions */}
        <div className="col-span-12 lg:col-span-4 cell-strong p-6">
          <div className="overline text-[var(--ink-soft)]">/ Quick actions</div>
          <div className="font-display text-2xl mt-1 mb-4">Jump in</div>
          <div className="space-y-3">
            <Link to="/dashboard/email" className="btn-secondary w-full justify-between" data-testid="qa-process-emails">
              Process my emails <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/dashboard/leads" className="btn-secondary w-full justify-between" data-testid="qa-upload-csv">
              Upload leads CSV <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/dashboard/bot" className="btn-secondary w-full justify-between" data-testid="qa-train-bot">
              Train chat bot <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/pricing" className="btn-accent w-full justify-between" data-testid="qa-upgrade">
              Upgrade plan <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function KindIcon({ kind }) {
  const map = {
    email: EnvelopeSimple, lead: Users, chat: ChatCircleDots, billing: CheckCircle,
  };
  const Icon = map[kind] || Clock;
  return (
    <div className="w-8 h-8 border border-[var(--ink)] flex items-center justify-center shrink-0">
      <Icon size={16} weight="duotone" />
    </div>
  );
}

function Module({ to, icon: Icon, n, title, copy, span, dark, accent }) {
  const cls = dark
    ? "cell-strong bg-[var(--ink)] text-white"
    : accent
    ? "cell-strong"
    : "cell-strong bg-white";
  const bg = accent ? { background: "var(--accent)" } : {};
  return (
    <Link to={to} className={`${span} ${cls} p-6 group transition-transform hover:-translate-y-1`} style={bg} data-testid={`module-${title.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div className={`overline ${dark ? "text-white/50" : "text-[var(--ink-soft)]"}`}>/ {n}</div>
        <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
      </div>
      <div className={`w-12 h-12 mt-3 flex items-center justify-center ${dark ? "bg-[var(--accent)]" : accent ? "bg-[var(--ink)]" : "bg-[var(--ink)]"}`}>
        <Icon size={22} weight="duotone" color={dark ? "#0A0A0A" : "#FFD600"} />
      </div>
      <div className="font-display text-2xl mt-3">{title}</div>
      <div className={`text-sm mt-1 ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{copy}</div>
    </Link>
  );
}
